import { getOfflineStorage, OfflineStorage } from './storage';
import { getNetworkService, NetworkService } from './network';
import {
    createSessionCacheRecord,
    credentialKey,
    getCachedSessionRecord,
    isAutoLoginUsable,
    normalizeSessionUser,
    resolveCachedUserId
} from './auth-session';
import $ from 'jquery';

interface SyncQueueWriteParams {
    entity_type: string;
    entity_id: string;
    action: string;
    payload: any;
    api_endpoint?: string;
    operation_id?: string;
    client_id?: string;
    base_revision?: string | number;
    payload_hash?: string;
    replace_actions?: string[];
}

interface SyncQueueRetryFilter {
    entity_type?: string;
    entity_id?: string;
    action?: string;
}

function stableJson(value: any): string {
    if (value === null || value === undefined) return 'null';
    if (typeof value !== 'object') return JSON.stringify(value);
    if (Array.isArray(value)) return `[${value.map(item => stableJson(item)).join(',')}]`;

    const keys = Object.keys(value).sort();
    return `{${keys.map(key => `${JSON.stringify(key)}:${stableJson(value[key])}`).join(',')}}`;
}

function fallbackHash(input: string): string {
    let hash = 2166136261;
    for (let i = 0; i < input.length; i++) {
        hash ^= input.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
    }
    return `fnv1a:${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

async function digestPayload(value: any): Promise<string> {
    const text = stableJson(value);
    const cryptoApi = (window as any).crypto;
    if (!cryptoApi || !cryptoApi.subtle || typeof TextEncoder === 'undefined') {
        return fallbackHash(text);
    }

    const bytes = new TextEncoder().encode(text);
    const digest = await cryptoApi.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
}

function parseOperations(value: any): any[] {
    if (Array.isArray(value)) return value;
    if (typeof value !== 'string' || !value.trim()) return [];
    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

type ChecklistResponseStatus = 'draft' | 'submit_pending' | 'submitted' | 'sync_failed';

function checklistResponseKey(responseId: string): string {
    return `checklist_response:${responseId}`;
}

function checklistResultKey(responseId: string): string {
    return `checklist_result:${responseId}`;
}

function checklistMappingKey(mappingId: string): string {
    return `checklist_mapping_response:${mappingId}`;
}

function checklistTimestamp(): string {
    return new Date().toISOString().slice(0, 19).replace('T', ' ');
}

function parseJsonValue(value: any, fallback: any): any {
    if (value === null || value === undefined || value === '') return fallback;
    if (typeof value !== 'string') return value;
    try {
        return JSON.parse(value);
    } catch {
        return fallback;
    }
}

function countChecklistPhotos(answers: any): number {
    if (!answers || typeof answers !== 'object') return 0;
    let count = 0;
    for (const answer of Object.values(answers)) {
        const photos = answer && typeof answer === 'object' ? (answer as any).photos : null;
        if (Array.isArray(photos)) count += photos.length;
    }
    return count;
}

function normalizeChecklistStatus(status: any): ChecklistResponseStatus {
    if (status === 'submitted') return 'submitted';
    if (status === 'submit_pending') return 'submit_pending';
    if (status === 'sync_failed') return 'sync_failed';
    return 'draft';
}

function isAuthError(error: any): boolean {
    return !!error && (Number(error.httpStatus || 0) === 401 || Number(error.httpStatus || 0) === 403);
}

function syncResponseError(res: any): any {
    const code = Number(res?.code || 0);
    const message = res?.message || res?.data || (code ? `Server error ${code}` : 'Server error');
    const error: any = new Error(String(message));
    error.httpStatus = code;
    error.serverMessage = message;
    return error;
}

export class SyncService {
    private storage: OfflineStorage;
    private network: NetworkService;
    private _syncing = false;
    private _listeners: ((pending: number) => void)[] = [];
    private _unsubNetwork: (() => void) | null = null;

    constructor() {
        this.storage = getOfflineStorage();
        this.network = getNetworkService();
        this._unsubNetwork = this.network.onStatusChange(async (status) => {
            if (status === 'online' && !this._syncing) {
                await this.syncAll();
            }
        });
    }

    onPendingChange(listener: (pending: number) => void): () => void {
        this._listeners.push(listener);
        return () => {
            this._listeners = this._listeners.filter(fn => fn !== listener);
        };
    }

    private async notifyPending() {
        const items = await this.storage.getPendingSyncItems();
        this._listeners.forEach(fn => {
            try { fn(items.length); } catch (e) { }
        });
    }

    async getPendingCount(): Promise<number> {
        const items = await this.storage.getPendingSyncItems();
        return items.length;
    }

    private matchesRetryFilter(item: any, filter: SyncQueueRetryFilter): boolean {
        if (!item) return false;
        if (filter.entity_type && item.entity_type !== filter.entity_type) return false;
        if (filter.entity_id && item.entity_id !== filter.entity_id) return false;
        if (filter.action && item.action !== filter.action) return false;
        return true;
    }

    async retryFailedItems(filter: SyncQueueRetryFilter = {}): Promise<{ success: number; failed: number; retried: number }> {
        const items = await this.storage.getSyncQueueItems();
        const failedItems = items.filter((item: any) =>
            item.status === 'failed' && this.matchesRetryFilter(item, filter)
        );

        for (const item of failedItems) {
            await this.storage.updateSyncItem(item.id, {
                status: 'pending',
                retry_count: 0,
                error: '',
                updated_at: Date.now()
            });
        }

        await this.notifyPending();

        if (typeof navigator !== 'undefined' && navigator.onLine && this.network.status === 'offline') {
            this.network.setStatus('syncing');
        }

        const result = await this.syncAll();
        return { ...result, retried: failedItems.length };
    }

    private async normalizeQueueItem(params: SyncQueueWriteParams): Promise<SyncQueueWriteParams> {
        const payload = params.payload || {};
        const payloadHash = params.payload_hash || await digestPayload(payload);
        const operations = parseOperations(payload.operations);
        const operationIds = operations
            .map((operation: any) => operation && (operation.operation_id || operation.op_id))
            .filter((id: any) => !!id);

        const operationId = params.operation_id
            || payload.operation_id
            || (operationIds.length === 1 ? operationIds[0] : '')
            || (operationIds.length > 1 ? `batch:${fallbackHash(operationIds.join('|'))}` : '')
            || `${params.entity_type}:${params.entity_id}:${payloadHash}`;

        return {
            ...params,
            operation_id: String(operationId),
            client_id: String(params.client_id || payload.client_id || ''),
            base_revision: params.base_revision ?? payload.base_revision ?? '',
            payload_hash: payloadHash
        };
    }

    async queueWrite(params: SyncQueueWriteParams) {
        const normalized = await this.normalizeQueueItem(params);
        if (Array.isArray(normalized.replace_actions) && normalized.replace_actions.length > 0) {
            const items = await this.storage.getSyncQueueItems();
            for (const item of items) {
                if (
                    item &&
                    item.status !== 'syncing' &&
                    item.entity_type === normalized.entity_type &&
                    item.entity_id === normalized.entity_id &&
                    normalized.replace_actions.includes(item.action)
                ) {
                    await this.storage.removeSyncItem(item.id);
                }
            }
        }
        await this.storage.addToSyncQueue(normalized);
        await this.notifyPending();
    }

    async syncAll(): Promise<{ success: number; failed: number }> {
        const startedAt = Date.now();
        const errors: any[] = [];

        if (this._syncing || !this.network.isOnline) {
            await this.storage.setMeta('sync:last_result', {
                started_at: startedAt,
                finished_at: Date.now(),
                success: 0,
                failed: 0,
                skipped: true,
                reason: this._syncing ? 'already_syncing' : 'offline'
            });
            return { success: 0, failed: 0 };
        }

        this._syncing = true;
        this.network.setStatus('syncing');
        let success = 0;
        let failed = 0;

        try {
            const items = await this.storage.getPendingSyncItems();
            for (const item of items) {
                try {
                    await this.storage.updateSyncItem(item.id, { status: 'syncing' });
                    await this.executeSyncItem(item);
                    await this.storage.removeSyncItem(item.id);
                    success++;
                } catch (err: any) {
                    if (isAuthError(err)) {
                        const errorMessage = err.message || '로그인이 필요합니다.';
                        const errorMeta = {
                            item_id: item.id,
                            entity_type: item.entity_type,
                            entity_id: item.entity_id,
                            action: item.action,
                            api_endpoint: item.api_endpoint,
                            retry_count: item.retry_count || 0,
                            auth_required: true,
                            error: errorMessage,
                            occurred_at: Date.now()
                        };
                        errors.push(errorMeta);
                        await this.storage.setMeta('sync:last_error', errorMeta);
                        await this.storage.updateSyncItem(item.id, {
                            status: 'pending',
                            error: errorMessage,
                            auth_required: true,
                            updated_at: Date.now()
                        });
                        failed++;
                        await this.notifyPending();
                        continue;
                    }
                    const retryCount = (item.retry_count || 0) + 1;
                    const errorMessage = err.message || String(err);
                    const errorMeta = {
                        item_id: item.id,
                        entity_type: item.entity_type,
                        entity_id: item.entity_id,
                        action: item.action,
                        api_endpoint: item.api_endpoint,
                        retry_count: retryCount,
                        final_failure: retryCount >= 3,
                        error: errorMessage,
                        occurred_at: Date.now()
                    };
                    errors.push(errorMeta);
                    await this.storage.setMeta('sync:last_error', errorMeta);
                    await this.handleSyncItemFailure(item, err, retryCount >= 3);
                    if (retryCount >= 3) {
                        await this.storage.updateSyncItem(item.id, {
                            status: 'failed',
                            retry_count: retryCount,
                            error: errorMessage,
                            updated_at: Date.now()
                        });
                    } else {
                        await this.storage.updateSyncItem(item.id, {
                            status: 'pending',
                            retry_count: retryCount,
                            error: errorMessage,
                            updated_at: Date.now()
                        });
                    }
                    failed++;
                }
                await this.notifyPending();
            }
        } finally {
            this._syncing = false;
            const serverReachable = typeof navigator === 'undefined'
                || navigator.onLine !== false
                || await this.network.checkConnectivity();
            this.network.setStatus(serverReachable ? 'online' : 'offline');
            await this.storage.setMeta('sync:last_result', {
                started_at: startedAt,
                finished_at: Date.now(),
                success,
                failed,
                errors
            });
            await this.notifyPending();
        }

        return { success, failed };
    }

    private isInspectionSave(item: any): boolean {
        return item && item.entity_type === 'inspection_save' && item.action === 'save';
    }

    private isLegacySyncPushEndpoint(endpoint: string): boolean {
        return /\/api\/sync\/push(?:$|[/?#])/.test(endpoint);
    }

    private resolveEndpoint(item: any): string {
        const endpoint = String(item.api_endpoint || '');
        if (this.isInspectionSave(item) && endpoint && this.isLegacySyncPushEndpoint(endpoint)) {
            return endpoint.replace(/\/api\/sync\/push(?:\/)?(?:[?#].*)?$/, '/api/page.inspection.viewer/save');
        }
        return endpoint;
    }

    private resolveLoginEndpoint(endpoint: string): string {
        const match = String(endpoint || '').match(/^(.*)\/api\/[^/]+\/[^/?#]+(?:[?#].*)?$/);
        if (match) return `${match[1]}/api/component.auth.login/login`;
        if (/^https?:\/\//.test(endpoint)) {
            const url = new URL(endpoint);
            return `${url.origin}/api/component.auth.login/login`;
        }
        return '/api/component.auth.login/login';
    }

    async ensureServerSessionForEndpoint(endpoint: string): Promise<boolean> {
        const session = await getCachedSessionRecord(this.storage);
        const userId = resolveCachedUserId(session);
        if (!userId || !isAutoLoginUsable(session)) return false;

        const credential = await this.storage.get('auth', credentialKey(userId));
        const passwordHash = String(credential?.server_password_hash || '');
        if (!passwordHash) return false;

        const loginEndpoint = this.resolveLoginEndpoint(endpoint);
        const result = await new Promise<any>((resolve, reject) => {
            $.ajax({
                url: loginEndpoint,
                type: 'POST',
                data: {
                    id: userId,
                    password: passwordHash,
                    auto_login: 'true'
                },
                crossDomain: /^https?:\/\//.test(loginEndpoint),
                xhrFields: { withCredentials: true },
                timeout: 10000,
                success: (res: any) => resolve(res),
                error: (xhr: any, status: string, error: string) => {
                    const requestError: any = new Error(`${status}: ${error}`);
                    requestError.httpStatus = xhr ? xhr.status : 0;
                    requestError.statusText = status;
                    reject(requestError);
                }
            });
        });

        if (!result || Number(result.code || 0) !== 200 || !result.data) return false;

        const normalized = normalizeSessionUser(userId, result.data);
        await this.storage.put('auth', createSessionCacheRecord(userId, normalized, {
            autoLogin: true,
            existing: session,
            authMode: 'server',
            offline: false
        }));
        return true;
    }

    private buildRequestPayload(item: any): any {
        if (!this.isInspectionSave(item)) return item.payload;

        const payload = { ...(item.payload || {}) };
        payload.operation_id = payload.operation_id || item.operation_id;
        payload.client_id = payload.client_id || item.client_id;
        payload.base_revision = payload.base_revision ?? item.base_revision ?? '';
        payload.payload_hash = payload.payload_hash || item.payload_hash;
        return payload;
    }

    private async cacheInspectionSaveResult(item: any, res: any): Promise<void> {
        if (!this.isInspectionSave(item) || !res || res.code !== 200 || !res.data) return;
        const drawingId = item.entity_id || item.payload?.drawing_id;
        if (!drawingId) return;

        if (Array.isArray(res.data.damages)) {
            await this.storage.put('damages', {
                drawing_id: drawingId,
                data: res.data.damages,
                synced_at: Date.now()
            });
        }
        if (res.data.drawings) {
            await this.storage.put('annotations', {
                drawing_id: drawingId,
                data: res.data.drawings,
                synced_at: Date.now()
            });
        }
        if (res.data.sync) {
            await this.storage.setMeta(`inspection_viewer_sync:${drawingId}`, res.data.sync);
        }
        if (Array.isArray(res.data.overview_photos) && item.payload?.facility_id) {
            await this.storage.setMeta(`inspection_viewer_overview:${item.payload.facility_id}`, res.data.overview_photos);
        }

        const clientId = String(item.client_id || item.payload?.client_id || '');
        const operationId = String(item.operation_id || item.payload?.operation_id || '');
        if (clientId) {
            const draftId = clientId + ':' + drawingId;
            const draft = await this.storage.get('inspection_drafts', draftId);
            if (draft && String(draft.queued_operation_id || '') === operationId) {
                if (Number(draft.version || 0) <= Number(draft.queued_version || 0)) {
                    await this.storage.deleteInspectionDraft(draftId);
                } else {
                    draft.status = 'dirty';
                    draft.queued_operation_id = '';
                    draft.queued_version = 0;
                    draft.updated_at = Date.now();
                    await this.storage.put('inspection_drafts', draft);
                }
            }
        }
    }

    private isChecklistResponse(item: any): boolean {
        return item && item.entity_type === 'checklist_response';
    }

    private getChecklistResponseId(item: any): string {
        return String(item?.entity_id || item?.payload?.response_id || '').trim();
    }

    private getChecklistPayloadAnswers(item: any): any {
        return parseJsonValue(item?.payload?.answers, {});
    }

    private getPhotoSyncStatus(status: ChecklistResponseStatus, item: any, cached: any): string {
        const answers = this.getChecklistPayloadAnswers(item) || cached?.answers || {};
        const photoCount = countChecklistPhotos(answers);
        if (photoCount === 0) return 'none';
        if (status === 'submitted') return 'synced';
        if (status === 'sync_failed') return 'failed';
        if (status === 'submit_pending') return 'pending';
        return 'local';
    }

    private async updateChecklistMapping(mappingId: string, responseId: string, status: ChecklistResponseStatus): Promise<void> {
        if (!mappingId) return;
        await this.storage.setMeta(checklistMappingKey(mappingId), {
            response_id: responseId,
            status,
            cached_at: Date.now()
        });

        const facilities = await this.storage.getAll('facilities');
        for (const facility of facilities) {
            const checklists = Array.isArray(facility.checklists) ? facility.checklists : [];
            let changed = false;
            for (const mapping of checklists) {
                if (mapping && mapping.mapping_id === mappingId) {
                    mapping.response_id = responseId;
                    mapping.response_status = status;
                    changed = true;
                }
            }
            if (changed) await this.storage.put('facilities', facility);
        }
    }

    private async updateChecklistPhotoManifest(data: any, responseId: string, status: ChecklistResponseStatus, error: string): Promise<void> {
        const projectId = data?.project?.id || data?.facility?.project_id || '';
        if (!projectId || !this.storage.getCacheManifest || !this.storage.updateCacheManifestItem) return;

        const manifest = await this.storage.getCacheManifest(projectId);
        if (!manifest || !Array.isArray(manifest.items)) return;

        const nextStatus = status === 'sync_failed'
            ? 'failed'
            : status === 'submit_pending'
                ? 'pending'
                : 'cached';

        const targets = manifest.items.filter((item: any) =>
            item.category === 'checklist_photo'
            && (item.parent_id === responseId || String(item.id || '').includes(`checklist_photo:${responseId}:`))
        );

        for (const item of targets) {
            await this.storage.updateCacheManifestItem(projectId, {
                ...item,
                status: item.status === 'missing' && status === 'submitted' ? 'missing' : nextStatus,
                error: status === 'sync_failed' ? error : (item.status === 'missing' ? item.error : ''),
                cached_at: status === 'submitted' ? Date.now() : item.cached_at
            });
        }
    }

    private async updateChecklistCachedStatus(
        item: any,
        status: ChecklistResponseStatus,
        updates: any = {}
    ): Promise<void> {
        const responseId = this.getChecklistResponseId(item);
        if (!responseId) return;

        const responseKey = checklistResponseKey(responseId);
        const resultKey = checklistResultKey(responseId);
        const cachedResponse = await this.storage.getMeta(responseKey);
        const cachedResult = await this.storage.getMeta(resultKey);
        const cached = cachedResponse || cachedResult;
        if (!cached || !cached.response) return;

        const nextResponse = {
            ...cached.response,
            ...updates,
            status,
            sync_source: status === 'submitted' ? 'server' : 'local',
            sync_status: updates.sync_status || (status === 'submitted' ? 'synced' : (status === 'sync_failed' ? 'failed' : 'pending')),
            photo_sync_status: updates.photo_sync_status || this.getPhotoSyncStatus(status, item, cached),
            updated: updates.updated || cached.response.updated || checklistTimestamp()
        };
        if (status === 'submitted') {
            nextResponse.submitted_synced_at = checklistTimestamp();
            nextResponse.sync_error = '';
        }

        const next = {
            ...cached,
            answers: cached.answers || this.getChecklistPayloadAnswers(item),
            response: nextResponse
        };

        await this.storage.setMeta(responseKey, next);
        await this.storage.setMeta(resultKey, next);
        await this.updateChecklistMapping(nextResponse.mapping_id, responseId, status);
        await this.updateChecklistPhotoManifest(next, responseId, status, nextResponse.sync_error || '');
    }

    private async cacheChecklistResponseResult(item: any, res: any): Promise<void> {
        if (!this.isChecklistResponse(item) || !res || res.code !== 200) return;

        const data = res.data || {};
        const serverResponse = data.response || {};
        const photoSync = data.photo_sync || {};
        const photoFailed = photoSync.status === 'failed' || Number(photoSync.failed || 0) > 0;

        if (photoFailed) {
            throw new Error(photoSync.error || '체크리스트 사진 파일 동기화에 실패했습니다.');
        }

        if (item.action === 'submit_response') {
            await this.updateChecklistCachedStatus(item, 'submitted', {
                ...serverResponse,
                status: 'submitted',
                photo_sync_status: photoSync.status || this.getPhotoSyncStatus('submitted', item, null)
            });
            return;
        }

        if (item.action === 'save_response') {
            const responseId = this.getChecklistResponseId(item);
            const cached = responseId ? await this.storage.getMeta(checklistResponseKey(responseId)) : null;
            const cachedStatus = normalizeChecklistStatus(cached?.response?.status);
            if (cachedStatus === 'draft') {
                await this.updateChecklistCachedStatus(item, 'draft', {
                    ...serverResponse,
                    status: 'draft',
                    sync_status: 'synced',
                    photo_sync_status: photoSync.status || this.getPhotoSyncStatus('draft', item, cached)
                });
            }
        }
    }

    private async handleSyncItemFailure(item: any, err: any, finalFailure: boolean): Promise<void> {
        if (!this.isChecklistResponse(item) || item.action !== 'submit_response') return;

        const error = err?.message || String(err || '동기화 실패');
        await this.updateChecklistCachedStatus(item, finalFailure ? 'sync_failed' : 'submit_pending', {
            sync_error: error,
            sync_failed_at: finalFailure ? checklistTimestamp() : '',
            photo_sync_status: finalFailure ? this.getPhotoSyncStatus('sync_failed', item, null) : this.getPhotoSyncStatus('submit_pending', item, null)
        });
    }

    private async executeSyncItem(item: any): Promise<void> {
        const endpoint = this.resolveEndpoint(item);
        if (!endpoint) {
            throw new Error('No API endpoint specified');
        }

        const request = () => new Promise<any>((resolve, reject) => {
            $.ajax({
                url: endpoint,
                type: 'POST',
                data: this.buildRequestPayload(item),
                crossDomain: /^https?:\/\//.test(endpoint),
                xhrFields: {
                    withCredentials: true
                },
                timeout: this.isChecklistResponse(item) ? 60000 : 10000,
                success: (res: any) => {
                    if (res && Number(res.code || 0) >= 400) {
                        reject(syncResponseError(res));
                    } else {
                        resolve(res);
                    }
                },
                error: (xhr: any, status: string, error: string) => {
                    const requestError: any = new Error(`Network error: ${status} ${error}`);
                    requestError.httpStatus = xhr ? xhr.status : 0;
                    requestError.statusText = status;
                    try {
                        const body = typeof xhr?.responseText === 'string' ? JSON.parse(xhr.responseText) : xhr?.responseJSON;
                        requestError.serverMessage = body?.data || body?.message || '';
                    } catch { }
                    reject(requestError);
                }
            });
        });
        let res: any;
        try {
            res = await request();
        } catch (err: any) {
            if (!isAuthError(err) || !await this.ensureServerSessionForEndpoint(endpoint)) throw err;
            res = await request();
        }
        await this.cacheInspectionSaveResult(item, res);
        await this.cacheChecklistResponseResult(item, res);
    }

    destroy() {
        if (this._unsubNetwork) {
            this._unsubNetwork();
        }
    }
}

let _syncInstance: SyncService | null = null;

export function getSyncService(): SyncService {
    if (!_syncInstance) {
        _syncInstance = new SyncService();
    }
    return _syncInstance;
}
